"use client";

import React, {FC, ReactNode, useEffect, useRef, useState} from "react";
import {ChevronDown} from "lucide-react";
import {cn} from "@/shared/lib/utils";
import {Button} from "@/shared/components/ui/button";

export type SectionState = "always-open" | "collapsible-open" | "collapsible-closed";

export interface ModalSectionItemProps {
    title: string;
    content: ReactNode;
    state?: SectionState; // Domyślnie "always-open"
    className?: string;
    titleClassName?: string;
}

export interface ModalSectionProps {
    items: ModalSectionItemProps[];
    className?: string;
}

const ModalSectionItem: FC<ModalSectionItemProps & {
    isOpen: boolean;
    onToggle: () => void;
}> = ({
          title,
          content,
          state = "always-open",
          className,
          titleClassName,
          isOpen,
          onToggle,
      }) => {
    const isCollapsible = state === "collapsible-open" || state === "collapsible-closed";

    return (
        <div className={cn("w-full", className)}>
            {/* Dla collapsible: przycisk z tytułem po lewej, linią w środku, strzałką po prawej (wszystkie rozmiary ekranów) */}
            {isCollapsible ? (
                <Button
                    type="button"
                    variant="ghost"
                    onClick={onToggle}
                    className="w-full my-1 py-1 flex items-center gap-1 text-left hover:opacity-80 transition-opacity cursor-pointer"
                >
                    <span className={cn("text-xs lg:text-sm font-medium flex-shrink-0", titleClassName)}>{title}</span>
                    <div className="flex-1 border-t border-gray-600"></div>
                    <ChevronDown
                        className={cn(
                            "w-4 h-4 transition-transform duration-200 flex-shrink-0",
                            isOpen ? "transform rotate-180" : "transform rotate-0"
                        )}
                    />
                </Button>
            ) : (
                /* Dla always-open: div z tytułem po lewej, linią po prawej (bez strzałki) - wszystkie rozmiary ekranów */
                <div className="w-full my-1 py-1 flex items-center gap-1">
                    <span className={cn("text-xs lg:text-sm font-medium flex-shrink-0", titleClassName)}>{title}</span>
                    <div className="flex-1 border-t border-gray-600"></div>
                </div>
            )}

            {/* Content - zwijany jeśli collapsible i zwinięty (wszystkie rozmiary ekranów) */}
            <div
                className={cn(
                    "overflow-hidden transition-all duration-200",
                    isCollapsible && !isOpen ? "max-h-0 opacity-0" : "opacity-100",
                )}
            >
                <div className="lg:py-1">{content}</div>
            </div>
        </div>
    );
};

export const ModalSection: FC<ModalSectionProps> = ({
                                                        items,
                                                        className,
                                                    }) => {
    const [isLargeScreen, setIsLargeScreen] = useState(() => {
        if (typeof window !== "undefined") {
            return window.innerWidth >= 1024; // lg breakpoint
        }
        return true; // Domyślnie zakładamy duży ekran (SSR)
    });

    const [openItems, setOpenItems] = useState<Set<number>>(() => {
        // Sprawdź rozmiar ekranu przy inicjalizacji
        const isLarge = typeof window !== "undefined" ? window.innerWidth >= 1024 : true;

        // Domyślnie otwórz itemy zgodnie z ich state i rozmiarem ekranu
        const openIndexes = items
            .map((item, index) => {
                const state = item.state || "always-open";
                // Na dużym ekranie: wszystkie collapsible otwarte
                // Na małym ekranie: tylko always-open i collapsible-open
                if (state === "always-open" || state === "collapsible-open") {
                    return index;
                }
                if (state === "collapsible-closed" && isLarge) {
                    return index; // Na dużym ekranie otwarte
                }
                return -1;
            })
            .filter((index) => index >= 0);

        return new Set(openIndexes);
    });

    // Ref do śledzenia poprzedniego rozmiaru ekranu
    const previousIsLargeRef = useRef(isLargeScreen);

    // Wykrywaj zmiany rozmiaru ekranu
    useEffect(() => {
        const handleResize = () => {
            const isLarge = window.innerWidth >= 1024;
            const previousIsLarge = previousIsLargeRef.current;

            // Aktualizuj tylko jeśli rozmiar się zmienił
            if (isLarge !== previousIsLarge) {
                setIsLargeScreen(isLarge);
                previousIsLargeRef.current = isLarge;

                // Gdy przechodzimy na mały ekran, zwiń sekcje z collapsible-closed
                if (!isLarge) {
                    setOpenItems((prev) => {
                        const newSet = new Set(prev);
                        items.forEach((item, index) => {
                            const state = item.state || "always-open";
                            if (state === "collapsible-closed") {
                                newSet.delete(index);
                            }
                        });
                        return newSet;
                    });
                } else {
                    // Gdy przechodzimy na duży ekran, otwórz wszystkie collapsible
                    setOpenItems((prev) => {
                        const newSet = new Set(prev);
                        items.forEach((item, index) => {
                            const state = item.state || "always-open";
                            if (state === "collapsible-open" || state === "collapsible-closed") {
                                newSet.add(index);
                            }
                        });
                        return newSet;
                    });
                }
            }
        };

        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, [items]);

    const handleToggle = (index: number) => {
        const item = items[index];
        const state = item.state || "always-open";

        // Jeśli nie jest collapsible, nie można zwinąć
        if (state === "always-open") return;

        setOpenItems((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(index)) {
                newSet.delete(index);
            } else {
                newSet.add(index);
            }
            return newSet;
        });
    };

    return (
        <div className={cn("w-full", className)}>
            {items.map((item, index) => {
                const state = item.state || "always-open";
                const isOpen = state === "always-open" ? true : openItems.has(index);

                return (
                    <ModalSectionItem
                        key={index}
                        {...item}
                        isOpen={isOpen}
                        onToggle={() => handleToggle(index)}
                    />
                );
            })}
        </div>
    );
};

